import { observer } from 'mobx-react-lite';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useStores } from 'src/store';
import { useCurrentChargingStation } from 'src/hooks';
import { TabPanel } from '../../../components/tab-panel';
import { DetailsTabs } from './components/details-tabs';
import { Details } from './components/details';
import { Groups } from './components/groups';
import { Configurations } from './components/configurations';
import { Access } from './components/access';
import { Actions } from './components/actions';
import { DataTransfer } from 'src/components/data-transfer/data-transfer';
import { LocationStatusHeader } from './components/location-status-header';
import { Card } from '@mui/material';
import globalStyles from '../../../global-styles.module.scss';
import { ChargingStation } from 'src/store/model';
import { Loader } from 'src/components';
import styles from './locations.module.scss';

export const Locations = observer(() => {
  const { id } = useParams<{ id: string }>();
  const [value, setValue] = useState(0);
  const [locationChargingStations, setLocationChargingStations] = useState<
    ChargingStation[]
  >([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const [locationStatusData, setLocationStatusData] = useState({
    locationName: '',
    owners: [] as string[],
    installationCode: '',
  });
  const locationNameRef = useRef('');
  const ownersRef = useRef<string[]>([]);

  const {
    chargingStationStore,
    locationStore,
    installationStore,
    chargingStationConfigurationsStore,
  } = useStores();

  useEffect(() => {
    const locationName =
      chargingStationStore.currentChargingStationLocationName || '';
    const owners = chargingStationStore.chargingStationOwners || [];
    const code = installationStore.getInstallationCode(id || '') || '';

    locationNameRef.current = locationName;
    ownersRef.current = owners;

    setLocationStatusData({
      locationName: locationNameRef.current,
      owners: ownersRef.current,
      installationCode: code,
    });
  }, [
    id,
    chargingStationStore.currentChargingStationLocationName,
    chargingStationStore.chargingStationOwners,
    installationStore,
  ]);

  useEffect(() => {
    if (id) {
      installationStore.getInstallationJobs(id);
    }
  }, [id, installationStore]);

  useCurrentChargingStation(
    chargingStationStore,
    chargingStationConfigurationsStore,
    installationStore,
    locationStore,
  );

  const fetchChargingStations = useCallback(() => {
    chargingStationStore.clearLocationChargingStations();

    chargingStationStore
      .getChargingStationsForLocation(
        'location_id',
        id,
        1000, // Fetch a large number of stations at once
        undefined,
        false,
      )
      .then(() => {
        const chargingStationPromise =
          chargingStationStore.locationChargingStationSearchResult.map((ch) => {
            return chargingStationStore.getChargingStation(
              ch.charging_station?.id ?? '',
            );
          });

        Promise.all(chargingStationPromise).then(() => {
          const ch: ChargingStation[] =
            chargingStationStore.chargingStationSearchResult.filter((c) => {
              return c.charging_station?.location_id === id;
            });
          setLocationChargingStations(ch);
          setInitialLoad(false);
        });
      });
  }, [chargingStationStore, id]);

  useEffect(() => {
    fetchChargingStations();
  }, [fetchChargingStations]);

  useEffect(() => {
    if (locationChargingStations.length > 0) {
      const firstStation = locationChargingStations[0];
      const searchItem =
        chargingStationStore.locationChargingStationSearchResult.find(
          (item) => item.charging_station?.id === firstStation.id,
        );

      if (searchItem) {
        setLocationStatusData((prev) => ({
          ...prev,
          locationName: searchItem.location_name || prev.locationName,
          owners: searchItem.owner_emails
            ? Array.from(searchItem.owner_emails.values())
            : prev.owners,
        }));
      }
    }
  }, [
    locationChargingStations,
    chargingStationStore.locationChargingStationSearchResult,
  ]);

  return (
    <>
      <LocationStatusHeader locationStatusData={locationStatusData} />
      <DetailsTabs
        setValue={(v) => {
          if (v === 3 || v === 4) return;
          setValue(v);
        }}
      />
      <TabPanel value={value} index={0}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Details chargingStations={locationChargingStations} />
        )}
      </TabPanel>
      <TabPanel value={value} index={1}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Groups chargingStations={locationChargingStations} />
        )}
      </TabPanel>
      <TabPanel value={value} index={2}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Configurations chargingStations={locationChargingStations} />
        )}
      </TabPanel>
      <TabPanel value={value} index={3}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Access />
        )}
      </TabPanel>
      <TabPanel value={value} index={4}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Actions chargingStations={locationChargingStations} />
        )}
      </TabPanel>
      <TabPanel value={value} index={5}>
        {initialLoad || chargingStationStore.isLoading ? (
          <div className={styles.loader}>
            <Loader />
          </div>
        ) : (
          <Card
            className={globalStyles.container}
            style={{ paddingBottom: 25 }}>
            <DataTransfer chargingStations={locationChargingStations} />
          </Card>
        )}
      </TabPanel>
    </>
  );
});
